/* ═══════════════════════════════════════════════════════
   SwipeHome — Saved Searches (Feature 9)
   Buyers save the current filter set as a named search,
   revisit from Profile, toggle notifications, delete.
   ═══════════════════════════════════════════════════════ */

import { api } from './api.js';
import { navigate } from './router.js';
import { state } from './state.js';
import { escapeHtml, showToast } from './ui.js';

interface SavedSearch {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  notify_new_matches: boolean;
  created_at: string | null;
  last_checked_at: string | null;
}

/**
 * Save the current feed filter state as a named search. Called
 * from the "Save this search" button on the feed. The backend
 * generates a default name from the filter JSON if the client
 * doesn't supply one; this minimal client just sends the filters
 * and lets the backend name it.
 */
export async function saveCurrentSearch(): Promise<void> {
  const filters = state.searchFilters;
  try {
    const res = await api<SavedSearch>('POST', '/saved-searches', {
      filters,
      notify_new_matches: true,
    });
    showToast(`Saved: ${res.name}`, 'success');
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Save failed';
    showToast(msg, 'error');
  }
}

/**
 * Apply a saved search's filters to state.searchFilters and
 * navigate back to the feed. Called when the user taps a saved
 * search card on the Saved Searches screen.
 */
export function applySavedSearch(filters: Record<string, unknown>): void {
  // Shallow-merge over current filters, then navigate. Missing
  // fields stay at their current values so the user doesn't lose
  // sort/radius preferences that aren't part of the saved state.
  Object.assign(state.searchFilters, filters);
  state.searchActive = true;
  navigate('feed');
  // Kick the feed's search runner so results populate immediately
  setTimeout(() => {
    const w = window as unknown as { runSearch?: () => void };
    w.runSearch?.();
  }, 200);
}

export async function deleteSavedSearch(id: string): Promise<void> {
  try {
    await api('DELETE', `/saved-searches/${id}`);
    showToast('Saved search removed', 'info');
    // Re-render the screen
    renderSavedSearchesInto(document.getElementById('saved-searches-list'));
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Delete failed';
    showToast(msg, 'error');
  }
}

export async function toggleSavedSearchNotify(id: string, enable: boolean): Promise<void> {
  try {
    await api('PATCH', `/saved-searches/${id}`, { notify_new_matches: enable });
    showToast(enable ? 'Notifications on' : 'Notifications off', 'info');
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Update failed';
    showToast(msg, 'error');
  }
}

/**
 * Render the Saved Searches screen.
 */
export function renderSavedSearches(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in saved-searches-screen';
  div.innerHTML = `
    <div class="screen-header">
      <button class="compare-back-btn" onclick="goBack()">
        <i data-feather="arrow-left" width="18"></i>
      </button>
      <h1>Saved Searches</h1>
    </div>
    <div id="saved-searches-list" class="saved-searches-list">
      <div class="loading"><div class="spinner"></div></div>
    </div>
  `;
  setTimeout(() => {
    renderSavedSearchesInto(document.getElementById('saved-searches-list'));
    if (window.feather) window.feather.replace();
  }, 10);
  return div;
}

async function renderSavedSearchesInto(container: HTMLElement | null): Promise<void> {
  if (!container) return;
  try {
    const data = await api<{ items: SavedSearch[] }>('GET', '/saved-searches');
    const items = data.items || [];
    if (items.length === 0) {
      container.innerHTML = `
        <div class="empty-state" style="padding:40px 16px;text-align:center">
          <i data-feather="bookmark" width="48"></i>
          <h3>No saved searches yet</h3>
          <p>Set some filters on Discover and tap "Save this search" to see them here.</p>
        </div>
      `;
      if (window.feather) window.feather.replace();
      return;
    }

    const cards = items
      .map((s) => {
        const created = s.created_at ? new Date(s.created_at).toLocaleDateString() : '';
        const filtersJson = escapeHtml(JSON.stringify(s.filters));
        return `
          <div class="ss-card">
            <div class="ss-card-body" onclick='applySavedSearchRaw(${filtersJson})'>
              <div class="ss-card-name">${escapeHtml(s.name)}</div>
              <div class="ss-card-meta">Saved ${escapeHtml(created)}</div>
            </div>
            <div class="ss-card-actions">
              <label class="ss-toggle" title="Notify me about new matches">
                <input type="checkbox" ${s.notify_new_matches ? 'checked' : ''} onchange="toggleSavedSearchNotify('${escapeHtml(s.id)}', this.checked)">
                <span class="ss-toggle-track"><span class="ss-toggle-thumb"></span></span>
              </label>
              <button class="ss-delete" type="button" aria-label="Delete saved search" onclick="confirmDeleteSavedSearch('${escapeHtml(s.id)}')">
                <i data-feather="trash-2" width="16"></i>
              </button>
            </div>
          </div>
        `;
      })
      .join('');

    container.innerHTML = cards;
    if (window.feather) window.feather.replace();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to load saved searches';
    container.innerHTML = `<div class="empty-state" style="padding:40px 16px"><p class="text-danger">${escapeHtml(msg)}</p></div>`;
  }
}

/** Global wrapper so the inline onclick can JSON-decode the filters. */
export function applySavedSearchRaw(filters: Record<string, unknown>): void {
  applySavedSearch(filters);
}

export function confirmDeleteSavedSearch(id: string): void {
  // Lightweight confirm — native dialog keeps the UI simple.
  if (window.confirm('Remove this saved search?')) {
    void deleteSavedSearch(id);
  }
}
